import Page from "components/page/Page";
import { useTranslation } from "react-i18next";
import React, { useLayoutEffect, useRef, useState } from "react";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { ButtonNew as Button } from "components/buttonNew";
import Card from "components/card/Card";
import "./websiteBuilderOverview.scss";
import { Badge } from "components/badge/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { faCircleCheck, faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { useWebsiteBuilderLauncher } from "./useWebsiteBuilderLauncher";

export default function WebsiteBuilderOverview() {
  const windowSize = useWindowSize();
  const { t } = useTranslation();

  const websiteBuilderLauncher = useWebsiteBuilderLauncher();

  const [websiteExists] = useState<boolean>(false);

  const startBuildingWebsiteRef = useRef<HTMLDivElement>(null);
  const startBuildingWebsiteDebounceRef = useRef<NodeJS.Timeout>(null);
  const [startBuildingWebsitePosition, setStartBuildingWebsitePosition] = useState<number>(0);

  const startBuildingWebsiteHeight = windowSize.height - startBuildingWebsitePosition - 1;
  const useStartBuildingWebsiteHeight = !websiteExists && windowSize.height > 600;

  useLayoutEffect(() => {
    if (websiteExists || !startBuildingWebsiteRef.current) {
      return;
    }

    const update = () => setStartBuildingWebsitePosition(startBuildingWebsiteRef.current.getBoundingClientRect().y);

    update();

    const debouncedUpdate = () => {
      clearTimeout(startBuildingWebsiteDebounceRef.current);

      startBuildingWebsiteDebounceRef.current = setTimeout(update, 100);
    };

    const resizeObserver = new ResizeObserver(debouncedUpdate);

    resizeObserver.observe(startBuildingWebsiteRef.current);
    return () => {
      clearTimeout(startBuildingWebsiteDebounceRef.current);
      resizeObserver.disconnect();
    };
  }, [websiteExists]);

  function renderStartBuildingWebsite() {
    return (
      <div
        ref={startBuildingWebsiteRef}
        className="website-builder-overview-start-building-website"
        style={{
          height: useStartBuildingWebsiteHeight ? startBuildingWebsiteHeight : null,
        }}
      >
        <img
          className="overview-start-building-website-image"
          src="../../../public/images/websiteBuilder/configuration/overview/website_builder_illustration.png"
          alt={t("Website Builder Illustration")}
        />
        <div>
          <div className="overview-start-building-website-message">{t("Start building your website")}</div>
          <div className="overview-start-building-website-supporting-message">
            {t("Ready to bring your website to life? Start by choosing a template and customize it to fit your vision.")}
          </div>
        </div>
        <Button type="quaternary" onClick={() => websiteBuilderLauncher.launchWebsiteBuilder(websiteExists)}>
          {t("Get started")}
        </Button>
      </div>
    );
  }

  function renderManageWebsite() {
    return (
      <Card
        title={t("Website overview")}
        subtitle={t("Manage your website and its pages.")}
      >
        <Card.Section>
          <div className="website-builder-overview-manage-website-card-container">
            <Card>
              <Card.Section>
                <div className="overview-manage-website-card-content">
                  <div className="overview-manage-website-thumbnail-container">
                    <img
                      className="overview-manage-website-thumbnail"
                      src="../../../public/images/websiteBuilder/configuration/overview/website_builder_thumbnail.png"
                    />
                  </div>
                  <div className="overview-manage-website-card-information">
                    <div className="overview-manage-website-title-container">
                      <div className="overview-manage-website-title">{t("Rebel Creek Golf Club")}</div>
                      <Badge
                        iconLeft={<FontAwesomeIcon className="overview-manage-website-status-icon" icon={faCircle} />}
                        type="success"
                      >
                        {t("Published")}
                      </Badge>
                    </div>
                    <hr className="overview-manage-website-divider" />
                    <div className="overview-manage-website-domain-container">
                      <div className="overview-manage-website-domain-label">{t("Domain:")}</div>
                      <a
                        className="overview-manage-website-domain"
                        href="https://rebelcreekgolf.com"
                        target="_blank"
                        rel="noreferrer"
                      >
                        https://rebelcreekgolf.com
                      </a>
                      <FontAwesomeIcon className="overview-manage-website-domain-status-active" icon={faCircleCheck} />
                    </div>
                  </div>
                  <NavigationDropdownNew
                    hideChevron
                    label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                    rightAlign
                    sections={[]}
                  />
                </div>
              </Card.Section>
            </Card>
          </div>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Page
      className={useStartBuildingWebsiteHeight ? "website-builder-overview-no-page-padding-bottom" : null}
      narrow
      title={t("Overview")}
      subtitle={t("Manage your website, pages, and domain.")}
      primaryAction={{
        content: websiteBuilderLauncher.launchWebsiteBuilderContent(websiteExists),
        action: () => websiteBuilderLauncher.launchWebsiteBuilder(websiteExists),
      }}
      multipleActionDropdownAction={{
        label: t("Quick actions"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: "",
              handler: () => {},
              icon: undefined,
            },
          ],
        },
      }}
    >
      {!websiteExists ? renderStartBuildingWebsite() : renderManageWebsite()}
    </Page>
  );
}
